import React, { useCallback, useState } from 'react';
import { Calendar, momentLocalizer, Messages } from 'react-big-calendar';
import moment from 'moment';
import { withLocalization, withLocalizationProps } from '../../context/Config';
import CalendarEventItem from '../CalendarEventItem';
import { Event } from '../CalendarEventViewItem';
import CalendarToolbar, {
  OnFilterChangeType,
  CalendarView,
} from '../CalendarToolbar';
import Loading from '../Loading';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.less';

const localizer = momentLocalizer(moment);

export type CalendarProps = {
  events: Array<Event>;
  onFilterChange: OnFilterChangeType;
  date?: Date;
  height: string | number;
  loading: boolean;
  messages?: Messages;
  onEventItemClick?: (item: any) => void;
  onBook?: (item: any) => void;
  onCancel?: (item: any) => void;
  onReschedule?: (item: any) => void;
};

const TCalendar: React.FC<CalendarProps & withLocalizationProps> = ({
  events = [],
  onFilterChange,
  date: propsDate,
  height,
  loading = false,
  onEventItemClick = () => {},
  onBook = () => {},
  onCancel = () => {},
  onReschedule = () => {},
  momentLocale,
  messages,
}) => {
  const [calendarView, setCalendarView] = useState('month' as CalendarView);
  const [date, setDate] = useState(
    propsDate ? new Date(propsDate) : new Date(),
  );

  const changeCalendarView = useCallback((value) => {
    setCalendarView(value);
  }, []);

  const timeRangeFormat = useCallback(
    ({ eventStartDate, eventEndDate }, culture, locale) => {
      return `${locale.format(
        eventStartDate,
        'DD MMM YYYY',
        culture,
      )} - ${locale.format(eventEndDate, 'DD MMM YYYY', culture)}`;
    },
    [],
  );

  return (
    <div className="calendar-container">
      {loading ? (
        <Loading fullscreen />
      ) : (
        <Calendar
          selectable
          localizer={localizer}
          events={events}
          startAccessor={(event) => new Date(event.eventStartDate)}
          endAccessor={(event) => new Date(event.eventEndDate)}
          formats={{
            timeGutterFormat: 'HH:mm',
            agendaHeaderFormat: timeRangeFormat,
          }}
          view={calendarView}
          onView={(view) => changeCalendarView(view)}
          scrollToTime={new Date()}
          popup
          date={date}
          style={{ height }}
          components={{
            event: (props) => (
              <CalendarEventItem
                {...props.event}
                onItemClick={() => onEventItemClick(props.event)}
                onBook={() => onBook(props.event)}
                onCancel={() => onCancel(props.event)}
                onReschedule={() => onReschedule(props.event)}
              />
            ),
            toolbar: (prop) =>
              CalendarToolbar(
                {
                  date,
                  changeDate: setDate,
                  onFilterChange,
                  calendarView,
                  changeCalendarView,
                },
                prop,
              ),
          }}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: `${event.color}`,
            },
            className: 'event-wrapper',
          })}
          dayPropGetter={(_date) => ({ className: 'day-wrapper' })}
          culture={momentLocale}
          messages={messages}
        />
      )}
    </div>
  );
};

export default withLocalization(TCalendar);
